import { useEffect, useState, useMemo } from "react";
import api from "../../services/api";
import ITAModal from "./ITAModal";
import "../../assets/styles/ITAdmin/ITAReqsPg.css";

const toStatus = (s) => {
  if (s === "pending")  return "open";
  if (s === "approved") return "in_progress";
  if (s === "rejected") return "closed";
  if (s === "resolved") return "resolved";
  return s || "open";
};
const priColor = (p) => ({ Critical:"#dc2626", High:"#ef4444", Medium:"#d97706", Low:"#2563eb" }[p] || "#94a3b8");
const stChip   = (s) => ({ resolved:"chip-ok", closed:"chip-dn", in_progress:"chip-wn" }[s] || "chip-ip");
const fmtDate  = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }); } catch { return d; } };
const monthLabel = (d) => { if (!d) return "Unknown"; try { return new Date(d).toLocaleDateString("en-IN", { month:"long", year:"numeric" }); } catch { return "Unknown"; } };

function exportCSV(rows, filename) {
  const headers = ["Employee","Dept","Category","Subject","Priority","Status","Date","Remarks"];
  const lines = [headers.join(",")];
  rows.forEach(r => {
    const s = toStatus(r.status);
    lines.push([r.emp_name||r.empName||"", r.department||"", r.category||r.type||"",
      `"${(r.subject||String(r.detail||"").split("\n")[0]||"").replace(/"/g,'""')}"`,
      r.priority||"Medium", s, r.date ? new Date(r.date).toLocaleDateString("en-IN") : "",
      `"${(r.remarks||r.admin_remarks||"").replace(/"/g,'""')}"`].join(","));
  });
  const blob = new Blob([lines.join("\n")], { type:"text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function Badge({ label, count, color, active, onClick }) {
  return (
    <button onClick={onClick} style={{ display:"flex", flexDirection:"column", alignItems:"center",
      padding:"10px 14px", borderRadius:10, border:"none", cursor:"pointer",
      background: active ? color : "#f8fafc",
      boxShadow: active ? `0 4px 14px ${color}44` : "0 1px 4px rgba(0,0,0,.06)",
      transition:"all 0.18s ease", minWidth:72 }}>
      <span style={{ fontSize:20, fontWeight:800, color: active ? "#fff" : color }}>{count}</span>
      <span style={{ fontSize:10, fontWeight:600, color: active ? "rgba(255,255,255,.85)" : "#64748b", marginTop:2, textTransform:"capitalize" }}>
        {label.replace("_"," ")}
      </span>
    </button>
  );
}

export default function ITAReqsPg() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [replyModal, setReplyModal]     = useState(null);
  const [escalateModal, setEscalateModal] = useState(null);
  const [escalateNote, setEscalateNote] = useState("");
  const [escalateSending, setEscalateSending] = useState(false);
  const [suc, setSuc] = useState("");
  const [statusF, setStatusF]   = useState("all");
  const [priorityF, setPriorityF] = useState("all");
  const [monthF, setMonthF]     = useState("all");
  const [search, setSearch]     = useState("");

  const load = () => {
    setLoading(true);
    api.getITRequests()
      .then(d => { setRequests(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(load, []);

  const STATUSES   = ["all","open","in_progress","resolved","closed"];
  const PRIORITIES = ["all","Critical","High","Medium","Low"];
  const months = useMemo(() => {
    const set = new Set(requests.map(r => monthLabel(r.date||r.created_at)));
    return ["all", ...Array.from(set).filter(m => m !== "Unknown").sort((a,b) => new Date(b)-new Date(a))];
  }, [requests]);

  const filtered = useMemo(() => requests.filter(r => {
    const s = toStatus(r.status);
    if (statusF !== "all" && s !== statusF) return false;
    if (priorityF !== "all" && (r.priority||"Medium") !== priorityF) return false;
    if (monthF !== "all" && monthLabel(r.date||r.created_at) !== monthF) return false;
    if (search) {
      const q = search.toLowerCase();
      if (![r.req_id,r.emp_name,r.subject,r.detail,r.category,r.type].join(" ").toLowerCase().includes(q)) return false;
    }
    return true;
  }), [requests, statusF, priorityF, monthF, search]);

  const counts = useMemo(() => {
    const c = { all:requests.length, open:0, in_progress:0, resolved:0, closed:0 };
    requests.forEach(r => { const s = toStatus(r.status); if (c[s] !== undefined) c[s]++; });
    return c;
  }, [requests]);

  const priCounts = useMemo(() => {
    const c = { all:requests.length, Critical:0, High:0, Medium:0, Low:0 };
    requests.forEach(r => { const p = r.priority||"Medium"; if (c[p] !== undefined) c[p]++; });
    return c;
  }, [requests]);

  const handleReply = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await api.itRequestAction(replyModal.req_id||replyModal.rid||replyModal.id, fd.get("action"), fd.get("reply"));
      setReplyModal(null);
      setSuc("✅ Reply sent to employee!"); setTimeout(() => setSuc(""), 3000);
      load();
    } catch (err) { alert("Error: " + err.message); }
  };

  const handleEscalate = async () => {
    if (!escalateNote.trim()) { alert("Please describe why you are escalating."); return; }
    setEscalateSending(true);
    try {
      await api.itEscalateTicketToPM(escalateModal.req_id||escalateModal.rid||escalateModal.id, escalateNote.trim());
      setEscalateModal(null); setEscalateNote(""); setReplyModal(null);
      setSuc("✅ Ticket escalated to PM. They will send instructions back to you.");
      setTimeout(() => setSuc(""), 5000);
      load();
    } catch (err) { alert("Error: " + err.message); }
    setEscalateSending(false);
  };

  const parseSubject = (r) => r.subject || String(r.detail||"").split("\n")[0] || "—";
  const csvName = () => ["helpdesk", statusF!=="all"?statusF:"", priorityF!=="all"?priorityF:"", monthF!=="all"?monthF.replace(" ","_"):""].filter(Boolean).join("_") + ".csv";

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
        <div className="sec" style={{ margin:0 }}>Help Desk — Employee Tickets</div>
        <button onClick={() => exportCSV(filtered, csvName())}
          style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 16px",
            background:"#0f172a", color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:12, cursor:"pointer" }}>
          ⬇ Download CSV {filtered.length !== requests.length ? `(${filtered.length})` : ""}
        </button>
      </div>

      {suc && (
        <div style={{ background:"#f0fdf4", color:"#16a34a", border:"1px solid #bbf7d0",
          borderRadius:8, padding:"8px 14px", marginBottom:12, fontSize:13, fontWeight:600 }}>
          {suc}
        </div>
      )}

      <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:16 }}>
        {STATUSES.map(s => (
          <Badge key={s} label={s==="all"?"All Tickets":s} count={counts[s]??0}
            color={s==="open"?"#6366f1":s==="in_progress"?"#f59e0b":s==="resolved"?"#10b981":s==="closed"?"#ef4444":"#334155"}
            active={statusF===s} onClick={() => setStatusF(s)} />
        ))}
      </div>

      <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:16, alignItems:"center" }}>
        <div style={{ display:"flex", gap:6, alignItems:"center", background:"#fff",
          border:"1px solid #e2e8f0", borderRadius:8, padding:"6px 10px", flexWrap:"wrap" }}>
          <span style={{ fontSize:11, color:"#64748b", fontWeight:600, marginRight:2 }}>Priority:</span>
          {PRIORITIES.map(p => (
            <button key={p} onClick={() => setPriorityF(p)} style={{ padding:"3px 10px", borderRadius:6,
              border:"none", cursor:"pointer", fontSize:11, fontWeight:600,
              background: priorityF===p ? priColor(p) : "#f1f5f9",
              color: priorityF===p ? "#fff" : "#64748b", transition:"all 0.15s" }}>
              {p==="all" ? `All (${priCounts.all})` : `${p} (${priCounts[p]||0})`}
            </button>
          ))}
        </div>
        <select value={monthF} onChange={e => setMonthF(e.target.value)}
          style={{ padding:"8px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12, color:"#374151", background:"#fff", cursor:"pointer" }}>
          {months.map(m => <option key={m} value={m}>{m==="all"?"All Months":m}</option>)}
        </select>
        <input placeholder="🔍 Search tickets…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ padding:"8px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12, flex:1, minWidth:180, outline:"none" }} />
        {(statusF!=="all"||priorityF!=="all"||monthF!=="all"||search) && (
          <button onClick={() => { setStatusF("all"); setPriorityF("all"); setMonthF("all"); setSearch(""); }}
            style={{ padding:"7px 12px", border:"1px solid #fecaca", borderRadius:8,
              background:"#fef2f2", color:"#ef4444", fontSize:11, fontWeight:600, cursor:"pointer" }}>
            ✕ Reset
          </button>
        )}
      </div>

      {monthF !== "all" && (
        <div style={{ background:"linear-gradient(135deg,#1e1b4b,#4f46e5)", borderRadius:10,
          padding:"12px 18px", marginBottom:14, display:"flex", alignItems:"center", justifyContent:"space-between", color:"#fff" }}>
          <div>
            <div style={{ fontWeight:700, fontSize:14 }}>📅 {monthF}</div>
            <div style={{ fontSize:11, opacity:.8, marginTop:2 }}>{filtered.length} tickets this month</div>
          </div>
          <div style={{ display:"flex", gap:14 }}>
            {["open","in_progress","resolved","closed"].map(s => {
              const mc = filtered.filter(r => toStatus(r.status)===s).length;
              return mc>0 ? <div key={s} style={{ textAlign:"center" }}><div style={{ fontSize:18, fontWeight:800 }}>{mc}</div><div style={{ fontSize:9, opacity:.75, textTransform:"capitalize" }}>{s.replace("_"," ")}</div></div> : null;
            })}
          </div>
          <button onClick={() => exportCSV(filtered, `helpdesk_${monthF.replace(" ","_")}.csv`)}
            style={{ padding:"7px 14px", background:"rgba(255,255,255,.2)", border:"1px solid rgba(255,255,255,.3)",
              borderRadius:8, color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer" }}>
            ⬇ Download Month CSV
          </button>
        </div>
      )}

      <div className="cd">
        <div className="cd-h">
          <div className="cd-t">Tickets</div>
          <div className="cd-s">{filtered.length} shown of {requests.length}</div>
        </div>
        <div className="cd-b" style={{ overflowX:"auto", padding:0 }}>
          {loading ? (
            <div style={{ textAlign:"center", padding:40, color:"#94a3b8" }}>Loading tickets…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign:"center", padding:40, color:"#94a3b8" }}>No tickets match filters</div>
          ) : (
            <table className="tbl">
              <thead><tr>
                <th>Employee</th><th>Category</th><th>Subject</th>
                <th>Priority</th><th>Status</th><th>Date</th><th>Remarks</th><th>Action</th>
              </tr></thead>
              <tbody>
                {filtered.map(r => {
                  const s = toStatus(r.status);
                  const p = r.priority || "Medium";
                  const escalatedToPM  = !!r.handler_escalated_to_pm;
                  const pmHasResponded = !!r.pm_response_to_handler;
                  return (
                    <tr key={r.req_id||r.rid||r.id}
                      style={{ background: pmHasResponded?"#f0fdf4":escalatedToPM?"#fefce8":"transparent" }}>
                      <td style={{ fontWeight:500 }}>{r.emp_name||r.empName||"—"}</td>
                      <td style={{ fontSize:12, color:"#64748b" }}>{r.category||r.type||"—"}</td>
                      <td style={{ maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontSize:12 }}>{parseSubject(r)}</td>
                      <td><span style={{ background:priColor(p)+"18", color:priColor(p), border:`1px solid ${priColor(p)}44`, padding:"2px 8px", borderRadius:20, fontSize:11, fontWeight:700 }}>{p}</span></td>
                      <td><span className={`chip ${stChip(s)}`} style={{ textTransform:"capitalize" }}>{s.replace("_"," ")}</span></td>
                      <td style={{ fontSize:11, color:"#64748b", whiteSpace:"nowrap" }}>{fmtDate(r.date||r.created_at)}</td>
                      <td style={{ fontSize:11, color:"#64748b", maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.remarks||r.admin_remarks||"—"}</td>
                      <td>
                        <button className="btn btn-s" onClick={() => setReplyModal(r)}
                          style={pmHasResponded?{background:"#16a34a",borderColor:"#16a34a",color:"#fff"}:{}}>
                          {pmHasResponded ? "📋 PM Replied" : "Reply"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Reply Modal ── */}
      {replyModal && (() => {
        const escalatedToPM  = !!replyModal.handler_escalated_to_pm;
        const pmHasResponded = !!replyModal.pm_response_to_handler;
        return (
          <ITAModal open title={`Reply — ${parseSubject(replyModal)}`} onClose={() => setReplyModal(null)}>
            {/* Ticket info */}
            <div style={{ background:"#f8fafc", borderRadius:8, padding:"10px 12px", marginBottom:12 }}>
              <div style={{ fontSize:12, color:"#64748b" }}>
                <b>{replyModal.emp_name||replyModal.empName||"—"}</b> · {replyModal.category||replyModal.type||"—"}
              </div>
              <div style={{ fontSize:13, color:"#374151", marginTop:4, lineHeight:1.5 }}>
                {replyModal.description||replyModal.ticket_description||replyModal.detail||"No description"}
              </div>
            </div>

            {/* PM forwarded note (old flow) */}
            {replyModal.pm_note && (
              <div style={{ background:"#fef9c3", border:"1px solid #fde68a", borderRadius:8, padding:"10px 14px", marginBottom:12 }}>
                <div style={{ fontSize:11, fontWeight:700, color:"#92400e", marginBottom:4 }}>📋 PM's Note (internal — employee cannot see this)</div>
                <div style={{ fontSize:13, color:"#78350f", lineHeight:1.5 }}>{replyModal.pm_note}</div>
              </div>
            )}

            {/* PM responded to IT Admin escalation */}
            {pmHasResponded && (
              <div style={{ background:"#f0fdf4", border:"2px solid #86efac", borderRadius:8, padding:"12px 14px", marginBottom:14 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#16a34a", marginBottom:6 }}>✅ PM has responded — Action Required</div>
                <div style={{ fontSize:13, color:"#166534", lineHeight:1.6, background:"#fff", borderRadius:6, padding:"8px 12px", border:"1px solid #bbf7d0" }}>
                  {replyModal.pm_response_to_handler}
                </div>
                <div style={{ fontSize:11, color:"#16a34a", marginTop:8 }}>Review PM's instructions above and reply to the employee accordingly.</div>
              </div>
            )}

            {/* Waiting for PM */}
            {escalatedToPM && !pmHasResponded && (
              <div style={{ background:"#fefce8", border:"1px solid #fde68a", borderRadius:8, padding:"10px 14px", marginBottom:12 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#b45309", marginBottom:4 }}>⏳ Awaiting PM Response</div>
                <div style={{ fontSize:12, color:"#78350f" }}>You escalated this ticket to PM. They will send instructions back to you shortly.</div>
                {replyModal.handler_escalation_note && <div style={{ fontSize:11, color:"#92400e", marginTop:6 }}>Your note to PM: "{replyModal.handler_escalation_note}"</div>}
              </div>
            )}

            {replyModal.remarks && <div style={{ marginBottom:8, fontSize:11, color:"#7c3aed" }}><b>Previous reply:</b> {replyModal.remarks}</div>}

            <form onSubmit={handleReply}>
              <div className="fg">
                <label className="fl">Your Reply to Employee *</label>
                <textarea name="reply" className="fi" rows={4}
                  placeholder={pmHasResponded ? "Based on PM's instructions above, type your reply to the employee…" : "Type your reply to the employee…"}
                  required />
              </div>
              <div className="fg">
                <label className="fl">Action</label>
                <select name="action" className="fi" defaultValue="approved">
                  <option value="approved">Approve → Resolve</option>
                  <option value="resolved">Mark Resolved</option>
                  <option value="rejected">Close / Reject</option>
                  <option value="resend">Resend to Employee</option>
                </select>
              </div>
              <p style={{ fontSize:11, color:"#64748b", margin:"6px 0 12px", lineHeight:1.6 }}>
                📬 Employee will receive <b>only your reply</b> — not any PM notes.
              </p>
              <div className="fa" style={{ gap:8 }}>
                <button type="button" className="btn btn-o" onClick={() => setReplyModal(null)}>Cancel</button>
                {!escalatedToPM && (
                  <button type="button"
                    onClick={() => { setEscalateModal(replyModal); setReplyModal(null); }}
                    style={{ padding:"9px 16px", background:"#fff7ed", color:"#c2410c",
                      border:"1px solid #fed7aa", borderRadius:8, fontWeight:700, fontSize:12, cursor:"pointer" }}>
                    🔺 Escalate to PM
                  </button>
                )}
                <button type="submit" className="btn btn-p">Send Reply to Employee</button>
              </div>
            </form>
          </ITAModal>
        );
      })()}

      {/* ── Escalate to PM Modal ── */}
      {escalateModal && (
        <ITAModal open title="🔺 Escalate Ticket to PM" onClose={() => { setEscalateModal(null); setEscalateNote(""); }}>
          <div style={{ background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:8, padding:"10px 14px", marginBottom:14 }}>
            <div style={{ fontSize:11, fontWeight:700, color:"#c2410c", marginBottom:4 }}>TICKET</div>
            <div style={{ fontWeight:600, fontSize:13, color:"#1e293b" }}>
              {parseSubject(escalateModal)}
            </div>
            <div style={{ fontSize:12, color:"#64748b", marginTop:2 }}>Employee: <b>{escalateModal.emp_name||"—"}</b></div>
          </div>

          <div className="fg">
            <label className="fl">Why are you escalating this to PM? *</label>
            <textarea className="fi" rows={4}
              placeholder="Describe the issue and why it needs PM's involvement. This note goes to PM only — employee will NOT see it."
              value={escalateNote} onChange={e => setEscalateNote(e.target.value)} />
          </div>

          <div style={{ background:"#f8fafc", borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:12, color:"#64748b", lineHeight:1.8 }}>
            <div style={{ fontWeight:600, color:"#374151", marginBottom:4 }}>What happens next:</div>
            <div>① PM receives your note and reviews the ticket privately</div>
            <div>② PM sends instructions back to IT Admin (you)</div>
            <div>③ Employee gets notified: "Your ticket is under review"</div>
            <div>④ You receive PM's instructions and reply to the employee</div>
            <div>⑤ Employee sees IT Admin's final reply in their helpdesk</div>
          </div>

          <div className="fa">
            <button type="button" className="btn btn-o" onClick={() => { setEscalateModal(null); setEscalateNote(""); }}>Cancel</button>
            <button onClick={handleEscalate} disabled={escalateSending || !escalateNote.trim()}
              style={{ padding:"9px 20px", background:"#c2410c", color:"#fff", border:"none",
                borderRadius:8, fontWeight:700, fontSize:12, cursor:"pointer",
                opacity:(!escalateNote.trim()||escalateSending)?0.6:1 }}>
              {escalateSending ? "Escalating…" : "🔺 Send to PM"}
            </button>
          </div>
        </ITAModal>
      )}
    </div>
  );
}
